import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { FileText } from 'lucide-react';
import NotFound from '../components/NotFound';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Titles and descriptions for the standard legal pages, so each has its own search snippet even before it loads.
const LEGAL_META = {
  'privacy-policy': {
    title: 'Privacy Policy',
    description: 'How VOIP2 Limited collects, uses, shares and protects your personal data, and the rights you have over your information.',
  },
  'terms-of-service': {
    title: 'Terms and Conditions',
    description: 'The terms for using VOIP2 phone numbers, SIP trunks and related services: accounts, acceptable use, payment and termination.',
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    description: 'The cookies and storage the VOIP2 website and portal use (essential, security and optional analytics) and how to change your choice.',
  },
};

const plainText = (html) => (html || '').replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, ' ').replace(/<[^>]+>/g, ' ')
  .replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();

const metaFor = (slug, content) => {
  if (LEGAL_META[slug]) return LEGAL_META[slug];
  const text = plainText(content?.content).replace(/^Last Updated:\s*\S+ \d{4}\s*/i, '');
  return {
    title: content?.title || 'Legal',
    description: text.length > 155 ? `${text.slice(0, 152).replace(/\s+\S*$/, '')}...` : text,
  };
};

// The page title is the only h1: a heading in the stored text that repeats it is dropped, any other h1 becomes h2.
const bodyHtml = (html, title) => (html || '')
  .replace(/^\s*<h1[^>]*>([\s\S]*?)<\/h1>/i, (m, inner) => (plainText(inner).toLowerCase() === (title || '').toLowerCase() ? '' : m))
  .replace(/<h1(\s[^>]*)?>/gi, '<h2$1>').replace(/<\/h1>/gi, '</h2>');

const LegalPage = () => {
  const { slug } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLegalContent();
  }, [slug]);

  const fetchLegalContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${BACKEND_URL}/api/public/legal/${slug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load content');
        }
        return;
      }

      const data = await response.json();
      setContent(data);
    } catch (error) {
      console.error('Failed to fetch legal content:', error);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const meta = metaFor(slug, content);
  const seo = <Seo title={meta.title} description={meta.description} path={`/${slug}`} breadcrumbs={[{ name: meta.title, path: `/${slug}` }]} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
        {seo}
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-8"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error === 'Page not found' || (!content && !error)) {
    return <NotFound />;
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
        {seo}
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{meta.title}</h1>
            <p className="text-gray-600">
              The {meta.title.toLowerCase()} could not be loaded just now. Please try again later, or email{' '}
              <a href="mailto:hello@voip2.co.uk" className="text-blue-600 hover:underline">hello@voip2.co.uk</a> for a copy.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
      {seo}
      <div className="max-w-4xl mx-auto">
        <Card className="p-8 md:p-12">
          {/* Header */}
          <div className="border-b pb-6 mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{content.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>Version {content.version}</span>
              {content.updated_at && (
                <>
                  <span>•</span>
                  <span>Last Updated: {new Date(content.updated_at).toLocaleDateString('en-GB')}</span>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none break-words"
            dangerouslySetInnerHTML={{ __html: bodyHtml(content.content, content.title) }}
            style={{
              color: '#1f2937',
              lineHeight: '1.75'
            }}
          />

          {/* Footer */}
          <div className="border-t mt-12 pt-6">
            <p className="text-sm text-gray-600">
              If you have any questions about this {content.title.toLowerCase()}, please contact us at{' '}
              <a href="mailto:hello@voip2.co.uk" className="text-blue-600 hover:underline">
                hello@voip2.co.uk
              </a>
            </p>
          </div>
        </Card>
      </div>

      {/* CSS for HTML content */}
      <style jsx>{`
        .prose h1 {
          font-size: 2em;
          font-weight: 700;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: #0a1f2e;
        }
        .prose h2 {
          font-size: 1.5em;
          font-weight: 600;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: #0a1f2e;
        }
        .prose h3 {
          font-size: 1.25em;
          font-weight: 600;
          margin-top: 1.25em;
          margin-bottom: 0.5em;
          color: #0a1f2e;
        }
        .prose p {
          margin-bottom: 1em;
        }
        .prose ul, .prose ol {
          margin-left: 1.5em;
          margin-bottom: 1em;
        }
        .prose li {
          margin-bottom: 0.5em;
        }
        .prose strong {
          font-weight: 600;
          color: #0a1f2e;
        }
        .prose a {
          color: #2563eb;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #1d4ed8;
        }
      `}</style>
    </div>
  );
};

export default LegalPage;
